/**
 * Blocking busy overlay
 *
 * Full-viewport portal with a spinner card, shown while the flow waits on work the patient cannot
 * interrupt. One component for all three uses: the sign-up gate's loading screen, the Chatwoot SDK
 * open, and the BNPL checkout submit — which are the same backdrop and card, previously copied.
 *
 * `messages` rotates in order and holds on the last entry, so a long wait reads as progress rather
 * than as a frozen screen. A single-entry array is the static case.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { space } from '../../../../styles/onboardingUiTokens';
import { Body } from '../typography/Body';
import { LoadingSpinner } from './LoadingSpinner';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${space.md};
  background: ${getMediumRGBA(0.35)};
  pointer-events: all;
`;

const SpinnerStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.lg};
  box-sizing: border-box;
  max-width: calc(100vw - ${space.lg});
  border-radius: 12px;
  background: ${colors.white};
  box-shadow: 0 8px 32px ${getMediumRGBA(0.2)};
`;

/**
 * Fixed size, not a max: the card must not resize as the message changes, in either direction. A
 * width that tracked the text would have the modal breathing in and out once a second, and a message
 * that wrapped would change the height too.
 *
 * `messageSlotWidth` is the budget every caller's copy has to fit on one line. It is sized from the
 * longest string in use (237 px at 15 px Ubuntu) plus headroom, and kept small enough that the card
 * — this plus the surrounding `space.lg` padding — still clears a 320 px viewport with a margin.
 * `nowrap` enforces the one line; the ellipsis is the degradation if a font fallback with wider
 * metrics ever overruns the budget, which is less bad than a silent reflow.
 */
const messageSlotWidth = '15.5rem';

const MessageSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${messageSlotWidth};
  max-width: 100%;
  text-align: center;

  & > * {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export type OnboardingBusyOverlayProps = {
  open: boolean;
  /** Rendered in order, one per tick; the last entry holds for as long as the work runs. */
  messages: readonly string[];
  /** Tick length. Default 1000 ms (ERS-61 D3, widened from 500 ms after the real wait proved longer). */
  intervalMs?: number;
  /** Suppresses a flash when the work finishes fast. Pass 0 to show immediately. */
  showAfterMs?: number;
  /**
   * Stable accessible name. Never the rotating copy: the live region announces this once, while the
   * rotating line is `aria-hidden` — a text node changing once a second would otherwise fire one
   * screen-reader announcement per tick.
   */
  ariaLabel: string;
};

type BusyOverlayContentProps = Omit<OnboardingBusyOverlayProps, 'open'> &
  Required<Pick<OnboardingBusyOverlayProps, 'intervalMs' | 'showAfterMs'>>;

/**
 * The overlay itself. Mounted only while open, so its timers and its position in the message list
 * reset by unmounting rather than by writing state from an effect.
 *
 * @param {Object} props Resolved overlay configuration.
 * @return {Object | null} Portal overlay, or null while still inside `showAfterMs`.
 */
function BusyOverlayContent({ messages, intervalMs, showAfterMs, ariaLabel }: BusyOverlayContentProps) {
  const [visible, setVisible] = useState(showAfterMs <= 0);
  const [index, setIndex] = useState(0);

  // Length, not the array itself: a caller passing an inline literal would otherwise restart the
  // rotation on every render and it would never advance past the first message.
  const messageCount = messages.length;

  useEffect(() => {
    const showTimer = showAfterMs > 0 ? setTimeout(() => setVisible(true), showAfterMs) : undefined;

    // Advances until the last entry and then stops. Wrapping back to the first message would read as
    // "stuck in a loop", which is the opposite of what the rotation is for.
    const lastIndex = messageCount - 1;
    let current = 0;
    let tick: ReturnType<typeof setInterval> | undefined;
    if (lastIndex > 0) {
      tick = setInterval(() => {
        current += 1;
        setIndex(current);
        if (current >= lastIndex && tick) {
          clearInterval(tick);
        }
      }, intervalMs);
    }

    return () => {
      if (showTimer) {
        clearTimeout(showTimer);
      }
      if (tick) {
        clearInterval(tick);
      }
    };
  }, [messageCount, intervalMs, showAfterMs]);

  if (!visible || typeof document === 'undefined') {
    return null;
  }

  const message = messages[Math.min(index, messageCount - 1)] ?? '';

  return createPortal(
    <Backdrop role="status" aria-live="polite" aria-busy="true" aria-label={ariaLabel}>
      <SpinnerStack>
        <LoadingSpinner color={colors.primary} size={32} />
        <MessageSlot aria-hidden="true">
          <Body variant="document" tone="muted">
            {message}
          </Body>
        </MessageSlot>
      </SpinnerStack>
    </Backdrop>,
    document.body,
  );
}

/**
 * Renders the blocking overlay while `open` is true.
 *
 * @param {Object} props Overlay configuration.
 * @return {Object | null} The overlay, or null while closed.
 */
export function OnboardingBusyOverlay({
  open,
  messages,
  intervalMs = 1000,
  showAfterMs = 200,
  ariaLabel,
}: OnboardingBusyOverlayProps) {
  if (!open) {
    return null;
  }

  return (
    <BusyOverlayContent
      messages={messages}
      intervalMs={intervalMs}
      showAfterMs={showAfterMs}
      ariaLabel={ariaLabel}
    />
  );
}
